// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { VirtualNetworkGateways } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  VirtualNetworkGateway,
  VirtualNetworkGatewaysListNextOptionalParams,
  VirtualNetworkGatewaysListOptionalParams,
  VirtualNetworkGatewaysListResponse,
  VirtualNetworkGatewayConnectionListEntity,
  VirtualNetworkGatewaysListConnectionsNextOptionalParams,
  VirtualNetworkGatewaysListConnectionsOptionalParams,
  VirtualNetworkGatewaysListConnectionsResponse,
  VirtualNetworkGatewaysCreateOrUpdateOptionalParams,
  VirtualNetworkGatewaysCreateOrUpdateResponse,
  VirtualNetworkGatewaysGetOptionalParams,
  VirtualNetworkGatewaysGetResponse,
  VirtualNetworkGatewaysDeleteOptionalParams,
  TagsObject,
  VirtualNetworkGatewaysUpdateTagsOptionalParams,
  VirtualNetworkGatewaysUpdateTagsResponse,
  VirtualNetworkGatewaysResetOptionalParams,
  VirtualNetworkGatewaysResetResponse,
  VirtualNetworkGatewaysResetVpnClientSharedKeyOptionalParams,
  VpnClientParameters,
  VirtualNetworkGatewaysGeneratevpnclientpackageOptionalParams,
  VirtualNetworkGatewaysGeneratevpnclientpackageResponse,
  VirtualNetworkGatewaysGenerateVpnProfileOptionalParams,
  VirtualNetworkGatewaysGenerateVpnProfileResponse,
  VirtualNetworkGatewaysGetVpnProfilePackageUrlOptionalParams,
  VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse,
  VirtualNetworkGatewaysGetBgpPeerStatusOptionalParams,
  VirtualNetworkGatewaysGetBgpPeerStatusResponse,
  VirtualNetworkGatewaysSupportedVpnDevicesOptionalParams,
  VirtualNetworkGatewaysSupportedVpnDevicesResponse,
  VirtualNetworkGatewaysListRadiusSecretsOptionalParams,
  VirtualNetworkGatewaysListRadiusSecretsResponse,
  VirtualNetworkGatewaysGetLearnedRoutesOptionalParams,
  VirtualNetworkGatewaysGetLearnedRoutesResponse,
  VirtualNetworkGatewaysGetAdvertisedRoutesOptionalParams,
  VirtualNetworkGatewaysGetAdvertisedRoutesResponse,
  VirtualNetworkGatewaysGetResiliencyInformationOptionalParams,
  VirtualNetworkGatewaysGetResiliencyInformationResponse,
  VirtualNetworkGatewaysGetRoutesInformationOptionalParams,
  VirtualNetworkGatewaysGetRoutesInformationResponse,
  VpnClientIPsecParameters,
  VirtualNetworkGatewaysSetVpnclientIpsecParametersOptionalParams,
  VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse,
  VirtualNetworkGatewaysGetVpnclientIpsecParametersOptionalParams,
  VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse,
  VpnDeviceScriptParameters,
  VirtualNetworkGatewaysVpnDeviceConfigurationScriptOptionalParams,
  VirtualNetworkGatewaysVpnDeviceConfigurationScriptResponse,
  VirtualNetworkGatewaysStartPacketCaptureOptionalParams,
  VirtualNetworkGatewaysStartPacketCaptureResponse,
  VpnPacketCaptureStopParameters,
  VirtualNetworkGatewaysStopPacketCaptureOptionalParams,
  VirtualNetworkGatewaysStopPacketCaptureResponse,
  VirtualNetworkGatewaysGetFailoverAllTestDetailsOptionalParams,
  VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse,
  VirtualNetworkGatewaysGetFailoverSingleTestDetailsOptionalParams,
  VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse,
  VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationOptionalParams,
  VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse,
  ExpressRouteFailoverStopApiParameters,
  VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationOptionalParams,
  VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse,
  VirtualNetworkGatewaysGetVpnclientConnectionHealthOptionalParams,
  VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse,
  P2SVpnConnectionRequest,
  VirtualNetworkGatewaysDisconnectVirtualNetworkGatewayVpnConnectionsOptionalParams,
  VirtualNetworkGatewayMigrationParameters,
  VirtualNetworkGatewaysInvokePrepareMigrationOptionalParams,
  VirtualNetworkGatewaysInvokePrepareMigrationResponse,
  VirtualNetworkGatewaysInvokeExecuteMigrationOptionalParams,
  VirtualNetworkGatewaysInvokeExecuteMigrationResponse,
  VirtualNetworkGatewaysInvokeCommitMigrationOptionalParams,
  VirtualNetworkGatewaysInvokeCommitMigrationResponse,
  VirtualNetworkGatewaysInvokeAbortMigrationOptionalParams,
  VirtualNetworkGatewaysInvokeAbortMigrationResponse,
  VirtualNetworkGatewaysListNextResponse,
  VirtualNetworkGatewaysListConnectionsNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing VirtualNetworkGateways operations. */
export class VirtualNetworkGatewaysImpl implements VirtualNetworkGateways {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class VirtualNetworkGateways class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets all virtual network gateways by resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    options?: VirtualNetworkGatewaysListOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkGateway> {
    const iter = this.listPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    options?: VirtualNetworkGatewaysListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VirtualNetworkGateway[]> {
    let result: VirtualNetworkGatewaysListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    options?: VirtualNetworkGatewaysListOptionalParams,
  ): AsyncIterableIterator<VirtualNetworkGateway> {
    for await (const page of this.listPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Gets all the connections in a virtual network gateway.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  public listConnections(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysListConnectionsOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkGatewayConnectionListEntity> {
    const iter = this.listConnectionsPagingAll(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listConnectionsPagingPage(
          resourceGroupName,
          virtualNetworkGatewayName,
          options,
          settings,
        );
      },
    };
  }

  private async *listConnectionsPagingPage(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysListConnectionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VirtualNetworkGatewayConnectionListEntity[]> {
    let result: VirtualNetworkGatewaysListConnectionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listConnections(resourceGroupName, virtualNetworkGatewayName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listConnectionsNext(
        resourceGroupName,
        virtualNetworkGatewayName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listConnectionsPagingAll(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysListConnectionsOptionalParams,
  ): AsyncIterableIterator<VirtualNetworkGatewayConnectionListEntity> {
    for await (const page of this.listConnectionsPagingPage(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Creates or updates a virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to create or update virtual network gateway operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VirtualNetworkGateway,
    options?: VirtualNetworkGatewaysCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysCreateOrUpdateResponse>,
      VirtualNetworkGatewaysCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        parameters,
        options,
      },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysCreateOrUpdateResponse,
      OperationState<VirtualNetworkGatewaysCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates or updates a virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to create or update virtual network gateway operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VirtualNetworkGateway,
    options?: VirtualNetworkGatewaysCreateOrUpdateOptionalParams,
  ): Promise<VirtualNetworkGatewaysCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      virtualNetworkGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets the specified virtual network gateway by resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkGatewayName, options },
      getOperationSpec,
    );
  }

  /**
   * Deletes the specified virtual network gateway.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes the specified virtual network gateway.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysDeleteOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDelete(resourceGroupName, virtualNetworkGatewayName, options);
    return poller.pollUntilDone();
  }

  /**
   * Updates a virtual network gateway tags.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to update virtual network gateway tags.
   * @param options The options parameters.
   */
  async beginUpdateTags(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: TagsObject,
    options?: VirtualNetworkGatewaysUpdateTagsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysUpdateTagsResponse>,
      VirtualNetworkGatewaysUpdateTagsResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysUpdateTagsResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        parameters,
        options,
      },
      spec: updateTagsOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysUpdateTagsResponse,
      OperationState<VirtualNetworkGatewaysUpdateTagsResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Updates a virtual network gateway tags.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to update virtual network gateway tags.
   * @param options The options parameters.
   */
  async beginUpdateTagsAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: TagsObject,
    options?: VirtualNetworkGatewaysUpdateTagsOptionalParams,
  ): Promise<VirtualNetworkGatewaysUpdateTagsResponse> {
    const poller = await this.beginUpdateTags(
      resourceGroupName,
      virtualNetworkGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets all virtual network gateways by resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    options?: VirtualNetworkGatewaysListOptionalParams,
  ): Promise<VirtualNetworkGatewaysListResponse> {
    return this.client.sendOperationRequest({ resourceGroupName, options }, listOperationSpec);
  }

  /**
   * Gets all the connections in a virtual network gateway.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  private _listConnections(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysListConnectionsOptionalParams,
  ): Promise<VirtualNetworkGatewaysListConnectionsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkGatewayName, options },
      listConnectionsOperationSpec,
    );
  }

  /**
   * Resets the primary of the virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginReset(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysResetOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysResetResponse>,
      VirtualNetworkGatewaysResetResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysResetResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: resetOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysResetResponse,
      OperationState<VirtualNetworkGatewaysResetResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Resets the primary of the virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginResetAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysResetOptionalParams,
  ): Promise<VirtualNetworkGatewaysResetResponse> {
    const poller = await this.beginReset(resourceGroupName, virtualNetworkGatewayName, options);
    return poller.pollUntilDone();
  }

  /**
   * Resets the VPN client shared key of the virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginResetVpnClientSharedKey(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysResetVpnClientSharedKeyOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: resetVpnClientSharedKeyOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Resets the VPN client shared key of the virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginResetVpnClientSharedKeyAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysResetVpnClientSharedKeyOptionalParams,
  ): Promise<void> {
    const poller = await this.beginResetVpnClientSharedKey(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Generates VPN client package for P2S client of the virtual network gateway in the specified resource
   * group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to the generate virtual network gateway VPN client package
   *                   operation.
   * @param options The options parameters.
   */
  async beginGeneratevpnclientpackage(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnClientParameters,
    options?: VirtualNetworkGatewaysGeneratevpnclientpackageOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGeneratevpnclientpackageResponse>,
      VirtualNetworkGatewaysGeneratevpnclientpackageResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGeneratevpnclientpackageResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        parameters,
        options,
      },
      spec: generatevpnclientpackageOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGeneratevpnclientpackageResponse,
      OperationState<VirtualNetworkGatewaysGeneratevpnclientpackageResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Generates VPN client package for P2S client of the virtual network gateway in the specified resource
   * group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to the generate virtual network gateway VPN client package
   *                   operation.
   * @param options The options parameters.
   */
  async beginGeneratevpnclientpackageAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnClientParameters,
    options?: VirtualNetworkGatewaysGeneratevpnclientpackageOptionalParams,
  ): Promise<VirtualNetworkGatewaysGeneratevpnclientpackageResponse> {
    const poller = await this.beginGeneratevpnclientpackage(
      resourceGroupName,
      virtualNetworkGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Generates VPN profile for P2S client of the virtual network gateway in the specified resource group.
   * Used for IKEV2 and radius based authentication.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to the generate virtual network gateway VPN client package
   *                   operation.
   * @param options The options parameters.
   */
  async beginGenerateVpnProfile(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnClientParameters,
    options?: VirtualNetworkGatewaysGenerateVpnProfileOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGenerateVpnProfileResponse>,
      VirtualNetworkGatewaysGenerateVpnProfileResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGenerateVpnProfileResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        parameters,
        options,
      },
      spec: generateVpnProfileOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGenerateVpnProfileResponse,
      OperationState<VirtualNetworkGatewaysGenerateVpnProfileResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Generates VPN profile for P2S client of the virtual network gateway in the specified resource group.
   * Used for IKEV2 and radius based authentication.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Parameters supplied to the generate virtual network gateway VPN client package
   *                   operation.
   * @param options The options parameters.
   */
  async beginGenerateVpnProfileAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnClientParameters,
    options?: VirtualNetworkGatewaysGenerateVpnProfileOptionalParams,
  ): Promise<VirtualNetworkGatewaysGenerateVpnProfileResponse> {
    const poller = await this.beginGenerateVpnProfile(
      resourceGroupName,
      virtualNetworkGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets pre-generated VPN profile for P2S client of the virtual network gateway in the specified
   * resource group. The profile needs to be generated first using generateVpnProfile.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetVpnProfilePackageUrl(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnProfilePackageUrlOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse>,
      VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getVpnProfilePackageUrlOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse,
      OperationState<VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Gets pre-generated VPN profile for P2S client of the virtual network gateway in the specified
   * resource group. The profile needs to be generated first using generateVpnProfile.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetVpnProfilePackageUrlAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnProfilePackageUrlOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetVpnProfilePackageUrlResponse> {
    const poller = await this.beginGetVpnProfilePackageUrl(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * The GetBgpPeerStatus operation retrieves the status of all BGP peers.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetBgpPeerStatus(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetBgpPeerStatusOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetBgpPeerStatusResponse>,
      VirtualNetworkGatewaysGetBgpPeerStatusResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetBgpPeerStatusResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getBgpPeerStatusOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetBgpPeerStatusResponse,
      OperationState<VirtualNetworkGatewaysGetBgpPeerStatusResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * The GetBgpPeerStatus operation retrieves the status of all BGP peers.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetBgpPeerStatusAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetBgpPeerStatusOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetBgpPeerStatusResponse> {
    const poller = await this.beginGetBgpPeerStatus(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets a xml format representation for supported vpn devices.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  supportedVpnDevices(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysSupportedVpnDevicesOptionalParams,
  ): Promise<VirtualNetworkGatewaysSupportedVpnDevicesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkGatewayName, options },
      supportedVpnDevicesOperationSpec,
    );
  }

  /**
   * List all Radius servers with respective radius secrets from virtual network gateway
   * VpnClientConfiguration.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  listRadiusSecrets(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysListRadiusSecretsOptionalParams,
  ): Promise<VirtualNetworkGatewaysListRadiusSecretsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkGatewayName, options },
      listRadiusSecretsOperationSpec,
    );
  }

  /**
   * This operation retrieves a list of routes the virtual network gateway has learned, including routes
   * learned from BGP peers.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetLearnedRoutes(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetLearnedRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetLearnedRoutesResponse>,
      VirtualNetworkGatewaysGetLearnedRoutesResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetLearnedRoutesResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getLearnedRoutesOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetLearnedRoutesResponse,
      OperationState<VirtualNetworkGatewaysGetLearnedRoutesResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves a list of routes the virtual network gateway has learned, including routes
   * learned from BGP peers.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetLearnedRoutesAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetLearnedRoutesOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetLearnedRoutesResponse> {
    const poller = await this.beginGetLearnedRoutes(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation retrieves a list of routes the virtual network gateway is advertising to the
   * specified peer.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peer The IP address of the peer.
   * @param options The options parameters.
   */
  async beginGetAdvertisedRoutes(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peer: string,
    options?: VirtualNetworkGatewaysGetAdvertisedRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetAdvertisedRoutesResponse>,
      VirtualNetworkGatewaysGetAdvertisedRoutesResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetAdvertisedRoutesResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, peer, options },
      spec: getAdvertisedRoutesOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetAdvertisedRoutesResponse,
      OperationState<VirtualNetworkGatewaysGetAdvertisedRoutesResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves a list of routes the virtual network gateway is advertising to the
   * specified peer.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peer The IP address of the peer.
   * @param options The options parameters.
   */
  async beginGetAdvertisedRoutesAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peer: string,
    options?: VirtualNetworkGatewaysGetAdvertisedRoutesOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetAdvertisedRoutesResponse> {
    const poller = await this.beginGetAdvertisedRoutes(
      resourceGroupName,
      virtualNetworkGatewayName,
      peer,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation retrieves the resiliency information for an Express Route Gateway, including the
   * gateway's current resiliency score and recommendations to further improve the score
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetResiliencyInformation(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetResiliencyInformationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetResiliencyInformationResponse>,
      VirtualNetworkGatewaysGetResiliencyInformationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetResiliencyInformationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getResiliencyInformationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetResiliencyInformationResponse,
      OperationState<VirtualNetworkGatewaysGetResiliencyInformationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves the resiliency information for an Express Route Gateway, including the
   * gateway's current resiliency score and recommendations to further improve the score
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetResiliencyInformationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetResiliencyInformationOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetResiliencyInformationResponse> {
    const poller = await this.beginGetResiliencyInformation(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation retrieves the route set information for an Express Route Gateway based on their
   * resiliency
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetRoutesInformation(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetRoutesInformationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetRoutesInformationResponse>,
      VirtualNetworkGatewaysGetRoutesInformationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetRoutesInformationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getRoutesInformationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetRoutesInformationResponse,
      OperationState<VirtualNetworkGatewaysGetRoutesInformationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves the route set information for an Express Route Gateway based on their
   * resiliency
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetRoutesInformationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetRoutesInformationOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetRoutesInformationResponse> {
    const poller = await this.beginGetRoutesInformation(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * The Set VpnclientIpsecParameters operation sets the vpnclient ipsec policy for P2S client of virtual
   * network gateway in the specified resource group through Network resource provider.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param vpnclientIpsecParams Parameters supplied to the Begin Set vpnclient ipsec parameters of
   *                             Virtual Network Gateway P2S client operation through Network resource provider.
   * @param options The options parameters.
   */
  async beginSetVpnclientIpsecParameters(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    vpnclientIpsecParams: VpnClientIPsecParameters,
    options?: VirtualNetworkGatewaysSetVpnclientIpsecParametersOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse>,
      VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        vpnclientIpsecParams,
        options,
      },
      spec: setVpnclientIpsecParametersOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse,
      OperationState<VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * The Set VpnclientIpsecParameters operation sets the vpnclient ipsec policy for P2S client of virtual
   * network gateway in the specified resource group through Network resource provider.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param vpnclientIpsecParams Parameters supplied to the Begin Set vpnclient ipsec parameters of
   *                             Virtual Network Gateway P2S client operation through Network resource provider.
   * @param options The options parameters.
   */
  async beginSetVpnclientIpsecParametersAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    vpnclientIpsecParams: VpnClientIPsecParameters,
    options?: VirtualNetworkGatewaysSetVpnclientIpsecParametersOptionalParams,
  ): Promise<VirtualNetworkGatewaysSetVpnclientIpsecParametersResponse> {
    const poller = await this.beginSetVpnclientIpsecParameters(
      resourceGroupName,
      virtualNetworkGatewayName,
      vpnclientIpsecParams,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * The Get VpnclientIpsecParameters operation retrieves information about the vpnclient ipsec policy
   * for P2S client of virtual network gateway in the specified resource group through Network resource
   * provider.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The virtual network gateway name.
   * @param options The options parameters.
   */
  async beginGetVpnclientIpsecParameters(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnclientIpsecParametersOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse>,
      VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getVpnclientIpsecParametersOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse,
      OperationState<VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * The Get VpnclientIpsecParameters operation retrieves information about the vpnclient ipsec policy
   * for P2S client of virtual network gateway in the specified resource group through Network resource
   * provider.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The virtual network gateway name.
   * @param options The options parameters.
   */
  async beginGetVpnclientIpsecParametersAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnclientIpsecParametersOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetVpnclientIpsecParametersResponse> {
    const poller = await this.beginGetVpnclientIpsecParameters(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Gets a xml format representation for vpn device configuration script.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayConnectionName The name of the virtual network gateway connection for
   *                                            which the configuration script is generated.
   * @param parameters Parameters supplied to the generate vpn device script operation.
   * @param options The options parameters.
   */
  vpnDeviceConfigurationScript(
    resourceGroupName: string,
    virtualNetworkGatewayConnectionName: string,
    parameters: VpnDeviceScriptParameters,
    options?: VirtualNetworkGatewaysVpnDeviceConfigurationScriptOptionalParams,
  ): Promise<VirtualNetworkGatewaysVpnDeviceConfigurationScriptResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        virtualNetworkGatewayConnectionName,
        parameters,
        options,
      },
      vpnDeviceConfigurationScriptOperationSpec,
    );
  }

  /**
   * Starts packet capture on virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginStartPacketCapture(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysStartPacketCaptureOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysStartPacketCaptureResponse>,
      VirtualNetworkGatewaysStartPacketCaptureResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysStartPacketCaptureResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: startPacketCaptureOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysStartPacketCaptureResponse,
      OperationState<VirtualNetworkGatewaysStartPacketCaptureResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Starts packet capture on virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginStartPacketCaptureAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysStartPacketCaptureOptionalParams,
  ): Promise<VirtualNetworkGatewaysStartPacketCaptureResponse> {
    const poller = await this.beginStartPacketCapture(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Stops packet capture on virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Virtual network gateway packet capture parameters supplied to stop packet capture
   *                   on gateway.
   * @param options The options parameters.
   */
  async beginStopPacketCapture(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnPacketCaptureStopParameters,
    options?: VirtualNetworkGatewaysStopPacketCaptureOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysStopPacketCaptureResponse>,
      VirtualNetworkGatewaysStopPacketCaptureResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysStopPacketCaptureResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        parameters,
        options,
      },
      spec: stopPacketCaptureOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysStopPacketCaptureResponse,
      OperationState<VirtualNetworkGatewaysStopPacketCaptureResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Stops packet capture on virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param parameters Virtual network gateway packet capture parameters supplied to stop packet capture
   *                   on gateway.
   * @param options The options parameters.
   */
  async beginStopPacketCaptureAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    parameters: VpnPacketCaptureStopParameters,
    options?: VirtualNetworkGatewaysStopPacketCaptureOptionalParams,
  ): Promise<VirtualNetworkGatewaysStopPacketCaptureResponse> {
    const poller = await this.beginStopPacketCapture(
      resourceGroupName,
      virtualNetworkGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation retrieves the details of all the failover tests performed on the gateway for
   * different peering locations
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param typeParam The type of failover test
   * @param fetchLatest Fetch only the latest tests for each peering location
   * @param options The options parameters.
   */
  async beginGetFailoverAllTestDetails(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    typeParam: string,
    fetchLatest: boolean,
    options?: VirtualNetworkGatewaysGetFailoverAllTestDetailsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse>,
      VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        typeParam,
        fetchLatest,
        options,
      },
      spec: getFailoverAllTestDetailsOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse,
      OperationState<VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves the details of all the failover tests performed on the gateway for
   * different peering locations
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param typeParam The type of failover test
   * @param fetchLatest Fetch only the latest tests for each peering location
   * @param options The options parameters.
   */
  async beginGetFailoverAllTestDetailsAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    typeParam: string,
    fetchLatest: boolean,
    options?: VirtualNetworkGatewaysGetFailoverAllTestDetailsOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetFailoverAllTestDetailsResponse> {
    const poller = await this.beginGetFailoverAllTestDetails(
      resourceGroupName,
      virtualNetworkGatewayName,
      typeParam,
      fetchLatest,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation retrieves the details of a particular failover test performed on the gateway based on
   * the test Guid
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peeringLocation Peering location of the test
   * @param failoverTestId The unique Guid value which identifies the test
   * @param options The options parameters.
   */
  async beginGetFailoverSingleTestDetails(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peeringLocation: string,
    failoverTestId: string,
    options?: VirtualNetworkGatewaysGetFailoverSingleTestDetailsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse>,
      VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        peeringLocation,
        failoverTestId,
        options,
      },
      spec: getFailoverSingleTestDetailsOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse,
      OperationState<VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation retrieves the details of a particular failover test performed on the gateway based on
   * the test Guid
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peeringLocation Peering location of the test
   * @param failoverTestId The unique Guid value which identifies the test
   * @param options The options parameters.
   */
  async beginGetFailoverSingleTestDetailsAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peeringLocation: string,
    failoverTestId: string,
    options?: VirtualNetworkGatewaysGetFailoverSingleTestDetailsOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetFailoverSingleTestDetailsResponse> {
    const poller = await this.beginGetFailoverSingleTestDetails(
      resourceGroupName,
      virtualNetworkGatewayName,
      peeringLocation,
      failoverTestId,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation starts failover simulation on the gateway for the specified peering location
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peeringLocation Peering location of the test
   * @param options The options parameters.
   */
  async beginStartExpressRouteSiteFailoverSimulation(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peeringLocation: string,
    options?: VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse>,
      VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        peeringLocation,
        options,
      },
      spec: startExpressRouteSiteFailoverSimulationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse,
      OperationState<VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation starts failover simulation on the gateway for the specified peering location
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param peeringLocation Peering location of the test
   * @param options The options parameters.
   */
  async beginStartExpressRouteSiteFailoverSimulationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    peeringLocation: string,
    options?: VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationOptionalParams,
  ): Promise<VirtualNetworkGatewaysStartExpressRouteSiteFailoverSimulationResponse> {
    const poller = await this.beginStartExpressRouteSiteFailoverSimulation(
      resourceGroupName,
      virtualNetworkGatewayName,
      peeringLocation,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * This operation stops failover simulation on the gateway for the specified peering location
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param stopParameters Virtual network gateway stop simulation parameters supplied to stop failover
   *                       simulation on gateway.
   * @param options The options parameters.
   */
  async beginStopExpressRouteSiteFailoverSimulation(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    stopParameters: ExpressRouteFailoverStopApiParameters,
    options?: VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse>,
      VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        stopParameters,
        options,
      },
      spec: stopExpressRouteSiteFailoverSimulationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse,
      OperationState<VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * This operation stops failover simulation on the gateway for the specified peering location
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param stopParameters Virtual network gateway stop simulation parameters supplied to stop failover
   *                       simulation on gateway.
   * @param options The options parameters.
   */
  async beginStopExpressRouteSiteFailoverSimulationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    stopParameters: ExpressRouteFailoverStopApiParameters,
    options?: VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationOptionalParams,
  ): Promise<VirtualNetworkGatewaysStopExpressRouteSiteFailoverSimulationResponse> {
    const poller = await this.beginStopExpressRouteSiteFailoverSimulation(
      resourceGroupName,
      virtualNetworkGatewayName,
      stopParameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Get VPN client connection health detail per P2S client connection of the virtual network gateway in
   * the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetVpnclientConnectionHealth(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnclientConnectionHealthOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse>,
      VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: getVpnclientConnectionHealthOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse,
      OperationState<VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Get VPN client connection health detail per P2S client connection of the virtual network gateway in
   * the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param options The options parameters.
   */
  async beginGetVpnclientConnectionHealthAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysGetVpnclientConnectionHealthOptionalParams,
  ): Promise<VirtualNetworkGatewaysGetVpnclientConnectionHealthResponse> {
    const poller = await this.beginGetVpnclientConnectionHealth(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Disconnect vpn connections of virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param request The parameters are supplied to disconnect vpn connections.
   * @param options The options parameters.
   */
  async beginDisconnectVirtualNetworkGatewayVpnConnections(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    request: P2SVpnConnectionRequest,
    options?: VirtualNetworkGatewaysDisconnectVirtualNetworkGatewayVpnConnectionsOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, request, options },
      spec: disconnectVirtualNetworkGatewayVpnConnectionsOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Disconnect vpn connections of virtual network gateway in the specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param request The parameters are supplied to disconnect vpn connections.
   * @param options The options parameters.
   */
  async beginDisconnectVirtualNetworkGatewayVpnConnectionsAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    request: P2SVpnConnectionRequest,
    options?: VirtualNetworkGatewaysDisconnectVirtualNetworkGatewayVpnConnectionsOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDisconnectVirtualNetworkGatewayVpnConnections(
      resourceGroupName,
      virtualNetworkGatewayName,
      request,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Trigger prepare migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param migrationParams Parameters supplied to the Begin Prepare migration on basic vpn gateway
   *                        through Network resource provider.
   * @param options The options parameters.
   */
  async beginInvokePrepareMigration(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    migrationParams: VirtualNetworkGatewayMigrationParameters,
    options?: VirtualNetworkGatewaysInvokePrepareMigrationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysInvokePrepareMigrationResponse>,
      VirtualNetworkGatewaysInvokePrepareMigrationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysInvokePrepareMigrationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkGatewayName,
        migrationParams,
        options,
      },
      spec: invokePrepareMigrationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysInvokePrepareMigrationResponse,
      OperationState<VirtualNetworkGatewaysInvokePrepareMigrationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Trigger prepare migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param migrationParams Parameters supplied to the Begin Prepare migration on basic vpn gateway
   *                        through Network resource provider.
   * @param options The options parameters.
   */
  async beginInvokePrepareMigrationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    migrationParams: VirtualNetworkGatewayMigrationParameters,
    options?: VirtualNetworkGatewaysInvokePrepareMigrationOptionalParams,
  ): Promise<VirtualNetworkGatewaysInvokePrepareMigrationResponse> {
    const poller = await this.beginInvokePrepareMigration(
      resourceGroupName,
      virtualNetworkGatewayName,
      migrationParams,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Trigger execute migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeExecuteMigration(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeExecuteMigrationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysInvokeExecuteMigrationResponse>,
      VirtualNetworkGatewaysInvokeExecuteMigrationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysInvokeExecuteMigrationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: invokeExecuteMigrationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysInvokeExecuteMigrationResponse,
      OperationState<VirtualNetworkGatewaysInvokeExecuteMigrationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Trigger execute migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeExecuteMigrationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeExecuteMigrationOptionalParams,
  ): Promise<VirtualNetworkGatewaysInvokeExecuteMigrationResponse> {
    const poller = await this.beginInvokeExecuteMigration(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Trigger commit migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeCommitMigration(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeCommitMigrationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysInvokeCommitMigrationResponse>,
      VirtualNetworkGatewaysInvokeCommitMigrationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysInvokeCommitMigrationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: invokeCommitMigrationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysInvokeCommitMigrationResponse,
      OperationState<VirtualNetworkGatewaysInvokeCommitMigrationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Trigger commit migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeCommitMigrationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeCommitMigrationOptionalParams,
  ): Promise<VirtualNetworkGatewaysInvokeCommitMigrationResponse> {
    const poller = await this.beginInvokeCommitMigration(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Trigger abort migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeAbortMigration(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeAbortMigrationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkGatewaysInvokeAbortMigrationResponse>,
      VirtualNetworkGatewaysInvokeAbortMigrationResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkGatewaysInvokeAbortMigrationResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkGatewayName, options },
      spec: invokeAbortMigrationOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkGatewaysInvokeAbortMigrationResponse,
      OperationState<VirtualNetworkGatewaysInvokeAbortMigrationResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Trigger abort migration for the virtual network gateway.
   * @param resourceGroupName The resource group name of the virtual network gateway.
   * @param virtualNetworkGatewayName The name of the gateway.
   * @param options The options parameters.
   */
  async beginInvokeAbortMigrationAndWait(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    options?: VirtualNetworkGatewaysInvokeAbortMigrationOptionalParams,
  ): Promise<VirtualNetworkGatewaysInvokeAbortMigrationResponse> {
    const poller = await this.beginInvokeAbortMigration(
      resourceGroupName,
      virtualNetworkGatewayName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    nextLink: string,
    options?: VirtualNetworkGatewaysListNextOptionalParams,
  ): Promise<VirtualNetworkGatewaysListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listNextOperationSpec,
    );
  }

  /**
   * ListConnectionsNext
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkGatewayName The name of the virtual network gateway.
   * @param nextLink The nextLink from the previous successful call to the ListConnections method.
   * @param options The options parameters.
   */
  private _listConnectionsNext(
    resourceGroupName: string,
    virtualNetworkGatewayName: string,
    nextLink: string,
    options?: VirtualNetworkGatewaysListConnectionsNextOptionalParams,
  ): Promise<VirtualNetworkGatewaysListConnectionsNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkGatewayName, nextLink, options },
      listConnectionsNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    201: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    202: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    204: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters87,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    201: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const updateTagsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    201: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    202: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    204: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.resourceGroupName, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listConnectionsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/connections",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGatewayListConnectionsResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const resetOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/reset",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    201: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    202: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    204: {
      bodyMapper: Mappers.VirtualNetworkGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.gatewayVip],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const resetVpnClientSharedKeyOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/resetvpnclientsharedkey",
  httpMethod: "POST",
  responses: {
    200: {},
    201: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const generatevpnclientpackageOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/generatevpnclientpackage",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters88,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const generateVpnProfileOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/generatevpnprofile",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters88,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getVpnProfilePackageUrlOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getvpnprofilepackageurl",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getBgpPeerStatusOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getBgpPeerStatus",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.BgpPeerStatusListResult,
    },
    201: {
      bodyMapper: Mappers.BgpPeerStatusListResult,
    },
    202: {
      bodyMapper: Mappers.BgpPeerStatusListResult,
    },
    204: {
      bodyMapper: Mappers.BgpPeerStatusListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.peer],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const supportedVpnDevicesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/supportedvpndevices",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listRadiusSecretsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/listRadiusSecrets",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.RadiusAuthServerListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getLearnedRoutesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getLearnedRoutes",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    201: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    202: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    204: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getAdvertisedRoutesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getAdvertisedRoutes",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    201: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    202: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    204: {
      bodyMapper: Mappers.GatewayRouteListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.peer1],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getResiliencyInformationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getResiliencyInformation",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.GatewayResiliencyInformation,
    },
    201: {
      bodyMapper: Mappers.GatewayResiliencyInformation,
    },
    202: {
      bodyMapper: Mappers.GatewayResiliencyInformation,
    },
    204: {
      bodyMapper: Mappers.GatewayResiliencyInformation,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.attemptRefresh],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getRoutesInformationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getRoutesInformation",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.GatewayRouteSetsInformation,
    },
    201: {
      bodyMapper: Mappers.GatewayRouteSetsInformation,
    },
    202: {
      bodyMapper: Mappers.GatewayRouteSetsInformation,
    },
    204: {
      bodyMapper: Mappers.GatewayRouteSetsInformation,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.attemptRefresh],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const setVpnclientIpsecParametersOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/setvpnclientipsecparameters",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    201: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    202: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    204: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.vpnclientIpsecParams,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getVpnclientIpsecParametersOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getvpnclientipsecparameters",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    201: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    202: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    204: {
      bodyMapper: Mappers.VpnClientIPsecParameters,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const vpnDeviceConfigurationScriptOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/connections/{virtualNetworkGatewayConnectionName}/vpndeviceconfigurationscript",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters89,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayConnectionName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const startPacketCaptureOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/startPacketCapture",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.ErrorModel,
    },
  },
  requestBody: Parameters.parameters90,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const stopPacketCaptureOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/stopPacketCapture",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.ErrorModel,
    },
  },
  requestBody: Parameters.parameters91,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getFailoverAllTestDetailsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getFailoverAllTestsDetails",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverTestDetails",
            },
          },
        },
      },
    },
    201: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverTestDetails",
            },
          },
        },
      },
    },
    202: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverTestDetails",
            },
          },
        },
      },
    },
    204: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverTestDetails",
            },
          },
        },
      },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.typeParam, Parameters.fetchLatest],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getFailoverSingleTestDetailsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getFailoverSingleTestDetails",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverSingleTestDetails",
            },
          },
        },
      },
    },
    201: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverSingleTestDetails",
            },
          },
        },
      },
    },
    202: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverSingleTestDetails",
            },
          },
        },
      },
    },
    204: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: {
              name: "Composite",
              className: "ExpressRouteFailoverSingleTestDetails",
            },
          },
        },
      },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.peeringLocation, Parameters.failoverTestId],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const startExpressRouteSiteFailoverSimulationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/startSiteFailoverTest",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.peeringLocation],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const stopExpressRouteSiteFailoverSimulationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/stopSiteFailoverTest",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    201: {
      bodyMapper: { type: { name: "String" } },
    },
    202: {
      bodyMapper: { type: { name: "String" } },
    },
    204: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.stopParameters,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getVpnclientConnectionHealthOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/getVpnClientConnectionHealth",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.VpnClientConnectionHealthDetailListResult,
    },
    201: {
      bodyMapper: Mappers.VpnClientConnectionHealthDetailListResult,
    },
    202: {
      bodyMapper: Mappers.VpnClientConnectionHealthDetailListResult,
    },
    204: {
      bodyMapper: Mappers.VpnClientConnectionHealthDetailListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const disconnectVirtualNetworkGatewayVpnConnectionsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/disconnectVirtualNetworkGatewayVpnConnections",
  httpMethod: "POST",
  responses: {
    200: {},
    201: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.request2,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const invokePrepareMigrationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/prepareMigration",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokePrepareMigrationHeaders,
    },
    201: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokePrepareMigrationHeaders,
    },
    202: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokePrepareMigrationHeaders,
    },
    204: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokePrepareMigrationHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.migrationParams,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const invokeExecuteMigrationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/executeMigration",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeExecuteMigrationHeaders,
    },
    201: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeExecuteMigrationHeaders,
    },
    202: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeExecuteMigrationHeaders,
    },
    204: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeExecuteMigrationHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const invokeCommitMigrationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/commitMigration",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeCommitMigrationHeaders,
    },
    201: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeCommitMigrationHeaders,
    },
    202: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeCommitMigrationHeaders,
    },
    204: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeCommitMigrationHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const invokeAbortMigrationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkGateways/{virtualNetworkGatewayName}/abortMigration",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeAbortMigrationHeaders,
    },
    201: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeAbortMigrationHeaders,
    },
    202: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeAbortMigrationHeaders,
    },
    204: {
      headersMapper: Mappers.VirtualNetworkGatewaysInvokeAbortMigrationHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listConnectionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkGatewayListConnectionsResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.virtualNetworkGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
